import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, setSearchTerm, setPage, updateUser } from '../../store/slices/usersSlice';
import {
  Box,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputAdornment,
  CircularProgress,
  Pagination,
  Stack,
  Link,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Flag as FlagIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { db } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import FeatureFlagsDialog from '../FeatureFlagsDialog';

function Users() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const {
    users,
    searchTerm,
    page,
    totalPages,
    loading,
    searchPerformed,
    usersPerPage
  } = useSelector((state) => state.users);

  // Local component state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [featureFlagsOpen, setFeatureFlagsOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Load persisted search results on component mount
  useEffect(() => {
    // If we have a search term but no results, re-fetch
    if (searchTerm && !users.length && searchPerformed) {
      dispatch(fetchUsers({ searchTerm, page, limit: usersPerPage }));
    }
  }, [dispatch, searchTerm, users.length, searchPerformed, page, usersPerPage]);

  const handleRowClick = (userId) => {
    navigate(`/users/${userId}`);
  };

  const handleMenuOpen = (event, user) => {
    event.stopPropagation(); // Prevent row click when clicking menu
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleShowFeatureFlags = () => {
    setFeatureFlagsOpen(true);
    handleMenuClose();
  };

  const handleSaveFeatureFlags = async (flags) => {
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        featureFlags: flags
      });
      
      // Update the user in Redux state
      dispatch(updateUser({ userId: selectedUser.id, updates: { featureFlags: flags } }));
      
      setSnackbarMessage('Feature flags updated successfully');
      setSnackbarOpen(true);
      setFeatureFlagsOpen(false);
    } catch (error) {
      console.error('Error updating feature flags:', error);
      setSnackbarMessage('Error updating feature flags');
      setSnackbarOpen(true);
    }
  };

  const handleShowConfig = () => {
    setConfigOpen(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setFeatureFlagsOpen(false);
    setConfigOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleCopyBillingUrl = async () => {
    if (selectedUser?.id) {
      const url = `https://pay.rev.cat/cwikwlzeuvsdmoey/${selectedUser.id}`;
      try {
        await navigator.clipboard.writeText(url);
        setSnackbarMessage('RevCat billing URL copied to clipboard');
        setSnackbarOpen(true);
        handleMenuClose();
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  const handleViewRevCatProfile = () => {
    if (selectedUser?.id) {
      window.open(`https://app.revenuecat.com/customers/f05fd91f/${selectedUser.id}`, '_blank', 'noopener,noreferrer');
      handleMenuClose();
    }
  };

  const handleViewFishLog = () => {
    if (selectedUser?.id) {
      navigate(`/users/${selectedUser.id}/fish-log`);
      handleMenuClose();
    }
  };

  const handlePageChange = async (event, value) => {
    dispatch(setPage(value));
    dispatch(fetchUsers({ searchTerm, page: value, limit: usersPerPage }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    
    dispatch(setPage(1));
    dispatch(fetchUsers({ searchTerm, page: 1, limit: usersPerPage }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <form onSubmit={handleSearch}>
          <TextField
            fullWidth
            label="Search Users"
            value={searchTerm}
            onChange={(e) => dispatch(setSearchTerm(e.target.value))}
            placeholder="Search by email, display name, or user ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: loading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )
            }}
          />
        </form>
      </Box>

      {searchPerformed && !loading && users.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
          No users found
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>UID</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Contact ID</TableCell>
                <TableCell>OS</TableCell>
                <TableCell>App Version</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow 
                  key={user.id}
                  onClick={() => handleRowClick(user.id)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user.id}
                  </TableCell>
                  <TableCell>{user.displayName || 'N/A'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.contactId ? (
                      <Link 
                        href={`https://mautic.blueskyinvention.com/s/contacts/view/${user.contactId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {user.contactId}
                      </Link>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>{user.platform?.os || 'N/A'}</TableCell>
                  <TableCell>{user.platform?.appVersion || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, user)}
                      sx={{ width: 'auto', minWidth: 'unset' }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleShowFeatureFlags}>
          <ListItemIcon>
            <FlagIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show Feature Flags</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleShowConfig}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show Configuration</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyBillingUrl}>
          <ListItemIcon>
            <PaymentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy RevCat Billing URL</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewRevCatProfile}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View RevCat Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewFishLog}>
          <ListItemIcon>
            <ListIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Fish Log</ListItemText>
        </MenuItem>
      </Menu>

      <FeatureFlagsDialog
        open={featureFlagsOpen}
        onClose={handleCloseDialog}
        user={selectedUser}
        onSave={handleSaveFeatureFlags}
      />

      <Dialog
        open={configOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configuration</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {selectedUser?.config ? 
              JSON.stringify(selectedUser.config, null, 2) : 
              'No configuration found'}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {users.length > 0 && (
        <Stack spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            color="primary"
          />
        </Stack>
      )}
    </Box>
  );
}

export default Users;
